/**
 * CollectorScanner - interfejs skanowania kodow kreskowych + dialog ilosci.
 * Wyodrebniony z KolektorPage.jsx.
 */

import React, { Component } from 'react';
import BarcodeScanner from './BarcodeScanner';

// Error Boundary dla obslugi bledow React
class ScannerErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Scanner Error Boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '1rem',
          backgroundColor: 'var(--danger-light)',
          color: 'var(--danger)',
          borderRadius: '8px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '0.5rem' }}>&#9888;&#65039;</div>
          <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Blad skanera</div>
          <div style={{ fontSize: '12px', marginBottom: '1rem' }}>
            {this.state.error?.message || 'Wystapil nieoczekiwany blad'}
          </div>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              border: '1px solid var(--danger)',
              color: 'var(--danger)'
            }}
          >
            Sprobuj ponownie
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Eksportujemy ErrorBoundary osobno - jest potrzebny tez w CollectorPriceView
export { ScannerErrorBoundary };

const CollectorScanner = ({
  currentSession,
  items,
  // Scanner state
  scanMode, setScanMode,
  isScannerActive, setIsScannerActive,
  manualEan, setManualEan,
  // Search state
  searchQuery, searchResults, searchLoading,
  // Handlers
  handleScan,
  handleManualScan,
  handleSearch,
  handleSelectProduct,
  setError,
  setSearchResults,
}) => (
  <div>
    {/* Info o sesji */}
    {currentSession && (
      <div style={{
        backgroundColor: 'var(--info-light)',
        padding: '0.75rem',
        borderRadius: '8px',
        marginBottom: '1rem',
        fontSize: '12px'
      }}>
        <strong>{currentSession.name}</strong>
        <span style={{ marginLeft: '8px', color: 'var(--text-muted)' }}>
          ({items.length} pozycji)
        </span>
      </div>
    )}

    {/* Przelacznik trybu */}
    <div style={{
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1rem'
    }}>
      <button
        onClick={() => { setScanMode('camera'); setIsScannerActive(true); setSearchResults([]); }}
        style={{
          flex: 1,
          padding: '6px 12px',
          border: `1px solid ${scanMode === 'camera' ? 'var(--info)' : 'var(--border)'}`,
          backgroundColor: scanMode === 'camera' ? 'var(--info-light)' : 'var(--card-bg)',
          color: scanMode === 'camera' ? 'var(--info)' : 'var(--text-secondary)',
          fontSize: '12px',
          fontWeight: scanMode === 'camera' ? '600' : '400'
        }}
      >
        Kamera
      </button>
      <button
        onClick={() => { setScanMode('manual'); setIsScannerActive(false); setSearchResults([]); }}
        style={{
          flex: 1,
          padding: '6px 12px',
          border: `1px solid ${scanMode === 'manual' ? 'var(--info)' : 'var(--border)'}`,
          backgroundColor: scanMode === 'manual' ? 'var(--info-light)' : 'var(--card-bg)',
          color: scanMode === 'manual' ? 'var(--info)' : 'var(--text-secondary)',
          fontSize: '12px',
          fontWeight: scanMode === 'manual' ? '600' : '400'
        }}
      >
        EAN
      </button>
      <button
        onClick={() => { setScanMode('search'); setIsScannerActive(false); }}
        style={{
          flex: 1,
          padding: '6px 12px',
          border: `1px solid ${scanMode === 'search' ? 'var(--info)' : 'var(--border)'}`,
          backgroundColor: scanMode === 'search' ? 'var(--info-light)' : 'var(--card-bg)',
          color: scanMode === 'search' ? 'var(--info)' : 'var(--text-secondary)',
          fontSize: '12px',
          fontWeight: scanMode === 'search' ? '600' : '400'
        }}
      >
        Szukaj
      </button>
    </div>

    {/* Skaner kamera */}
    {scanMode === 'camera' && (
      <div style={{
        backgroundColor: 'var(--card-bg)',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <ScannerErrorBoundary>
          <BarcodeScanner
            key="camera-scanner"
            onScan={(ean) => {
              try {
                handleScan(ean);
              } catch (err) {
                console.error('Blad w handleScan:', err);
                setError('Blad przetwarzania kodu');
              }
            }}
            onError={(err) => {
              console.error('Blad skanera:', err);
              setError(typeof err === 'string' ? err : 'Blad skanera');
            }}
            isActive={isScannerActive && !!currentSession}
          />
        </ScannerErrorBoundary>
        {!currentSession && (
          <div style={{
            textAlign: 'center',
            padding: '1rem',
            color: 'var(--danger)',
            fontSize: '14px'
          }}>
            Wybierz sesje aby rozpoczac skanowanie
          </div>
        )}
      </div>
    )}

    {/* Reczne wpisanie EAN */}
    {scanMode === 'manual' && (
      <div style={{
        backgroundColor: 'var(--card-bg)',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <input
          type="text"
          value={manualEan}
          onChange={(e) => setManualEan(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleManualScan()}
          placeholder="Wpisz kod EAN..."
          style={{
            width: '100%',
            padding: '1rem',
            fontSize: '18px',
            border: '2px solid var(--border)',
            borderRadius: '8px',
            textAlign: 'center',
            marginBottom: '0.75rem'
          }}
          autoFocus
        />
        <button
          onClick={handleManualScan}
          disabled={!manualEan.trim() || !currentSession}
          style={{
            width: '100%',
            padding: '6px 12px',
            border: '1px solid var(--success)',
            color: 'var(--success)',
            fontSize: '12px',
            opacity: (!manualEan.trim() || !currentSession) ? 0.5 : 1
          }}
        >
          Dodaj
        </button>
      </div>
    )}

    {/* Wyszukiwanie po nazwie */}
    {scanMode === 'search' && (
      <div style={{
        backgroundColor: 'var(--card-bg)',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Wpisz nazwe produktu..."
          style={{
            width: '100%',
            padding: '0.875rem',
            fontSize: '16px',
            border: '2px solid var(--purple)',
            borderRadius: '8px',
            marginBottom: '0.5rem'
          }}
          autoFocus
        />

        {/* Loading */}
        {searchLoading && (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>
            Szukam...
          </div>
        )}

        {/* Wyniki wyszukiwania */}
        {searchResults.length > 0 && (
          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid var(--border)',
            borderRadius: '8px'
          }}>
            {searchResults.map(product => (
              <div
                key={product.id}
                onClick={() => handleSelectProduct(product)}
                style={{
                  padding: '0.75rem',
                  borderBottom: '1px solid var(--border)',
                  cursor: 'pointer',
                  backgroundColor: 'var(--card-bg)',
                  transition: 'background-color 0.15s'
                }}
                onTouchStart={(e) => e.currentTarget.style.backgroundColor = 'var(--bg)'}
                onTouchEnd={(e) => e.currentTarget.style.backgroundColor = 'var(--card-bg)'}
              >
                <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--text)' }}>
                  {product.name}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {product.barcode && <span>EAN: {product.barcode} | </span>}
                  Stan: {product.stock_quantity || 0} {product.unit || 'szt'}
                  {product.price > 0 && <span> | {product.price.toFixed(2)} zl</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Brak wynikow */}
        {!searchLoading && searchQuery.length >= 2 && searchResults.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '1rem',
            color: 'var(--text-muted)',
            fontSize: '13px'
          }}>
            Nie znaleziono produktow
          </div>
        )}

        {/* Podpowiedz */}
        {searchQuery.length < 2 && (
          <div style={{
            textAlign: 'center',
            padding: '1rem',
            color: 'var(--text-muted)',
            fontSize: '12px'
          }}>
            Wpisz min. 2 znaki aby wyszukac
          </div>
        )}
      </div>
    )}

    {/* Ostatnie 5 pozycji */}
    {items.length > 0 && (
      <div style={{
        backgroundColor: 'var(--card-bg)',
        padding: '1rem',
        borderRadius: '8px'
      }}>
        <div style={{
          fontSize: '12px',
          fontWeight: '600',
          marginBottom: '0.5rem',
          color: 'var(--text-muted)'
        }}>
          Ostatnio dodane:
        </div>
        {items.slice(0, 5).map(item => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.5rem 0',
              borderBottom: '1px solid var(--border)',
              fontSize: '12px'
            }}
          >
            <div>
              <div style={{ fontWeight: '500' }}>{item.product_name}</div>
              <div style={{ color: 'var(--text-muted)' }}>{item.ean}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: '600' }}>{item.scanned_count} {item.unit}</div>
              <div style={{
                color: item.difference > 0 ? 'var(--success)' : item.difference < 0 ? 'var(--danger)' : 'var(--text-muted)',
                fontSize: '11px'
              }}>
                {item.difference > 0 ? '+' : ''}{item.difference}
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default CollectorScanner;
